import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, ArrowLeft, DollarSign } from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/app/components/ui/select';
import { usePayPeriods } from '@/hooks/use-payroll';
import { useOrgCurrency } from '@/hooks/use-settings';
import { apiClient } from '@/lib/api-client';
import { formatMoney } from '@/lib/currency';
import { queryKeys } from '@/lib/query-client';
import type { JobCostDetail } from '@/lib/types';

export function JobCostingDetailPage() {
  const orgCurrency = useOrgCurrency();
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const payPeriodsQuery = usePayPeriods();
  const payPeriods = payPeriodsQuery.data ?? [];
  const [selectedPeriodOverride, setSelectedPeriodOverride] = useState<string | null>(null);
  const latestPeriod = [...payPeriods].sort((a, b) =>
    b.end_date.localeCompare(a.end_date),
  )[0];
  const selectedPeriodId =
    selectedPeriodOverride ??
    (payPeriodsQuery.isLoading ? undefined : latestPeriod ? String(latestPeriod.id) : 'all');

  const selectedPeriod = payPeriods.find(
    (period) => String(period.id) === selectedPeriodId,
  );
  const isAllDates = selectedPeriodId === 'all';
  const dateParams = selectedPeriod
    ? { start_date: selectedPeriod.start_date, end_date: selectedPeriod.end_date }
    : undefined;
  const detailQuery = useQuery({
    queryKey: [
      ...queryKeys.jobCost.detail(Number(projectId)),
      {
        start_date: dateParams?.start_date,
        end_date: dateParams?.end_date,
      },
    ],
    queryFn: async (): Promise<JobCostDetail> => {
      const response = await apiClient.get<JobCostDetail>(
        `/api/v1/payroll/job-cost/${projectId}/`,
        { params: dateParams },
      );
      return response.data;
    },
    enabled:
      !!projectId &&
      !payPeriodsQuery.isLoading &&
      selectedPeriodId !== undefined,
  });
  const detail = detailQuery.data;
  const isLoading = payPeriodsQuery.isLoading || detailQuery.isLoading;

  const fmt = (val: string | null | undefined) => {
    if (val === null || val === undefined) return '-';
    const n = parseFloat(val);
    if (isNaN(n)) return val;
    return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };
  const compact = (val: string | null | undefined) => {
    if (val === null || val === undefined) return '-';
    const n = parseFloat(val);
    if (isNaN(n)) return val;
    return n.toLocaleString('en-US', { maximumFractionDigits: 2 });
  };
  const poBreakdown = detail?.purchase_order_breakdown ?? [];
  const excludedPOCosts = detail?.excluded_po_costs ?? [];
  const money = (val: string | number | null | undefined) =>
    val === null || val === undefined ? '-' : formatMoney(val, orgCurrency);

  const marginColor = (val: string | null | undefined) => {
    if (val === null || val === undefined) return '';
    const n = parseFloat(val);
    if (isNaN(n)) return '';
    if (n > 0) return 'text-green-600';
    if (n < 0) return 'text-red-600';
    return '';
  };

  if (isLoading) {
    return <div className="p-8 text-center text-gray-500">Loading project cost details...</div>;
  }

  if (!detail) {
    return <div className="p-8 text-center text-gray-500">Project not found</div>;
  }

  const budgetVariance = detail.budget
    ? parseFloat(detail.budget) - parseFloat(detail.total_cost)
    : null;

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/payroll/job-costing')}>
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back
        </Button>
      </div>

      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">{detail.project_name}</h1>
        <p className="text-gray-600 mt-1">Project {detail.project_number} &mdash; Cost Breakdown</p>
      </div>

      <div className="space-y-2">
        <label className="text-sm font-medium text-gray-700" htmlFor="job-cost-period">
          Cost period
        </label>
        <Select
          value={selectedPeriodId}
          onValueChange={setSelectedPeriodOverride}
          disabled={payPeriodsQuery.isLoading}
        >
          <SelectTrigger id="job-cost-period" aria-label="Pay period" className="max-w-md">
            <SelectValue placeholder="Loading pay periods..." />
          </SelectTrigger>
          <SelectContent>
            {payPeriods.map((period) => (
              <SelectItem key={period.id} value={String(period.id)}>
                {period.name} ({period.start_date} – {period.end_date})
              </SelectItem>
            ))}
            <SelectItem value="all">All dates</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {detail.pay_week_start_day !== undefined && (
        <p className="text-sm text-gray-600">
          Basis: approved time entries,{' '}
          {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'][
            detail.pay_week_start_day
          ] ?? 'Mon'}
          -week OT rule
        </p>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-gray-500">Total Cost</p>
            <p className="text-2xl font-bold text-gray-900">{money(detail.total_cost)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-gray-500">Revenue</p>
            <p className="text-2xl font-bold text-gray-900">{money(detail.revenue)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-gray-500">Profit</p>
            <p className={`text-2xl font-bold ${marginColor(detail.profit)}`}>
              {money(detail.profit)}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-gray-500">Margin</p>
            <p className={`text-2xl font-bold ${marginColor(detail.margin_percent)}`}>
              {detail.margin_percent !== null ? `${fmt(detail.margin_percent)}%` : '-'}
            </p>
          </CardContent>
        </Card>
      </div>

      {detail.labor_cost_incomplete && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-amber-800">
              Labor cost incomplete — {detail.missing_pay_rate_count} technician
              {detail.missing_pay_rate_count !== 1 ? 's' : ''} missing pay rates
            </p>
            {detail.missing_pay_rate_techs && detail.missing_pay_rate_techs.length > 0 && (
              <p className="text-sm text-amber-700 mt-1">
                {detail.missing_pay_rate_techs.join(', ')}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Budget Comparison */}
      {isAllDates && detail.budget && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Budget Comparison</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <p className="text-sm text-gray-500">Budget</p>
                <p className="text-lg font-semibold text-gray-900">{money(detail.budget)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Actual Cost</p>
                <p className="text-lg font-semibold text-gray-900">{money(detail.total_cost)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Variance</p>
                <p
                  className={`text-lg font-semibold ${
                    budgetVariance !== null && budgetVariance >= 0
                      ? 'text-green-600'
                      : 'text-red-600'
                  }`}
                >
                  {money(budgetVariance)}
                </p>
              </div>
            </div>
            {budgetVariance !== null && detail.budget && (
              <div className="mt-4">
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div
                    className={`h-3 rounded-full ${
                      parseFloat(detail.total_cost) <= parseFloat(detail.budget)
                        ? 'bg-green-500'
                        : 'bg-red-500'
                    }`}
                    style={{
                      width: `${Math.min(
                        (parseFloat(detail.total_cost) / parseFloat(detail.budget)) * 100,
                        100
                      )}%`,
                    }}
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {((parseFloat(detail.total_cost) / parseFloat(detail.budget)) * 100).toFixed(1)}%
                  of budget used
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Cost Breakdown */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-gray-500">Labor</p>
            <p className="text-xl font-bold text-gray-900">{money(detail.labor_cost)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-gray-500">Expenses</p>
            <p className="text-xl font-bold text-gray-900">{money(detail.expense_cost)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-gray-500">Purchase Orders</p>
            <p className="text-xl font-bold text-gray-900">
              {money(detail.purchase_order_cost ?? '0')}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-gray-500">Per Diem</p>
            <p className="text-xl font-bold text-gray-900">{money(detail.per_diem_total)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-gray-500">Mileage</p>
            <p className="text-xl font-bold text-gray-900">{money(detail.mileage_total)}</p>
          </CardContent>
        </Card>
      </div>

      {/* Labor Breakdown by Tech */}
      {detail.labor_breakdown.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <DollarSign className="h-5 w-5" />
              Labor Breakdown by Technician
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Technician
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Hours
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Rate / source
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Cost
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {detail.labor_breakdown.map((tech) => (
                    <tr
                      key={`${tech.tech_id}-${tech.rate_type}-${tech.rate}-${tech.rate_source}`}
                      className="hover:bg-gray-50"
                    >
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">
                        {tech.tech_name}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">{fmt(tech.hours)}h</td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        <span className="block">
                          {tech.rate_type === 'daily'
                          ? `${money(tech.rate)} / day × ${compact(tech.days)} ${
                              Number(tech.days) === 1 ? 'day' : 'days'
                            }`
                          : `${money(tech.rate)} / h · ${compact(tech.regular_hours)} h${
                              Number(tech.overtime_hours) > 0
                                ? ` + ${compact(tech.overtime_hours)} h OT @ ${compact(
                                    tech.overtime_multiplier,
                                )}×`
                                : ''
                            }`}
                        </span>
                        <span className="block text-xs text-gray-500">
                          {tech.rate_source_label ?? ({
                            worker_on_job: 'Worker on this job',
                            job: 'Job rate',
                            worker_default: 'Worker default',
                            missing: 'Missing rate',
                            chosen: 'Chosen rate',
                            custom: 'Custom rate',
                          }[tech.rate_source])}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">
                        {money(tech.cost)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Expense Breakdown by Category */}
      {detail.expense_breakdown.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Expense Breakdown by Category</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-gray-200">
              {detail.expense_breakdown.map((cat) => (
                <div
                  key={cat.category_name}
                  className="flex items-center justify-between px-4 py-3"
                >
                  <span className="text-sm text-gray-900">{cat.category_name}</span>
                  <span className="text-sm font-medium text-gray-900">{money(cat.total)}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Purchase order costs (received, tax included) */}
      {poBreakdown.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Purchase Order Costs</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-gray-200">
              {poBreakdown.map((po) => (
                <div
                  key={po.po_id}
                  className="flex items-center justify-between gap-4 px-4 py-3"
                >
                  <span className="text-sm text-gray-900">
                    {po.po_number}
                    {po.vendor_name ? ` · ${po.vendor_name}` : ''}
                    {!po.in_job_total && (
                      <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-xs text-amber-800">
                        Not in total
                      </span>
                    )}
                  </span>
                  <span className="text-sm font-medium text-gray-900">
                    {po.in_job_total ? money(po.total) : `${fmt(po.total)} ${po.currency}`}
                  </span>
                </div>
              ))}
            </div>
            {excludedPOCosts.length > 0 && (
              <p className="border-t px-4 py-3 text-xs text-gray-600" role="note">
                {`Purchase orders in ${excludedPOCosts
                  .map((row) => row.currency)
                  .join(', ')} are not added to the job total because they are not in the company currency (${orgCurrency}).`}
              </p>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
